import { generateInitials } from "@shopexperts/shared";

import type { PartnerDirectoryRecord } from "./partnerDirectoryApi.service";

// Single source of truth for "which image do we show for this scraped expert".
// The crawl-time upload (avatarMediaUrl) is canonical: the Shopify-hosted logo
// URL expires, which used to leave the dashboard with no image at all. The
// scraped URLs stay in the list purely as a runtime fallback while a record
// waits for its first successful upload.

/** Shape the avatar helper needs — a full record, a job-row record, or a hand-built stub. */
export type PartnerDirectoryAvatarInput = Pick<PartnerDirectoryRecord, "avatarMediaUrl" | "avatarUrl"> &
  Partial<Pick<PartnerDirectoryRecord, "name" | "mappedName" | "sourceSlug" | "rawPayload">>;

export interface PartnerDirectoryAvatarSources {
  /** Candidate image URLs, most durable first. Empty when nothing usable was scraped. */
  sources: string[];
  /** Display name the initials fallback is derived from. */
  name: string;
  /** Initials rendered when every candidate fails to load. */
  initials: string;
}

function httpUrlOrNull(value: unknown): string | null {
  if (typeof value !== "string") return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  return /^https?:\/\//i.test(trimmed) ? trimmed : null;
}

function scrapedLogoSrc(record: PartnerDirectoryAvatarInput): string | null {
  const profile = (record.rawPayload as { profile?: { logo?: { src?: string | null } | null } | null } | null | undefined)?.profile;
  return httpUrlOrNull(profile?.logo?.src);
}

export function partnerDirectoryAvatarName(record: PartnerDirectoryAvatarInput): string {
  const mappedName = record.mappedName?.trim();
  if (mappedName) return mappedName;

  return record.name?.trim() || record.sourceSlug?.trim() || "Unknown expert";
}

/**
 * Ordered avatar candidates for a partner directory record: the stored media
 * first, then the URL it was uploaded from, then the raw scraped logo.
 */
export function partnerDirectoryAvatarSources(record: PartnerDirectoryAvatarInput): PartnerDirectoryAvatarSources {
  const candidates = [httpUrlOrNull(record.avatarMediaUrl), httpUrlOrNull(record.avatarUrl), scrapedLogoSrc(record)];
  const sources = [...new Set(candidates.filter((candidate): candidate is string => candidate !== null))];
  const name = partnerDirectoryAvatarName(record);

  return { sources, name, initials: generateInitials(name) };
}

/** First usable avatar URL, or null when the record has no image at all. */
export function partnerDirectoryAvatarUrl(record: PartnerDirectoryAvatarInput): string | null {
  return partnerDirectoryAvatarSources(record).sources[0] ?? null;
}
